import PaginationComponent, { PaginationMode } from "slotworks/components/ui/pagination-component";
import ButtonElement, { ButtonEvent } from "slotworks/graphics/elements/button-element";
import Layers from "slotworks/graphics/layers/layers";
import SlotDefinition from "slotworks/model/slot-definition";
import { Services } from "slotworks/services/services";
import ControlOptions from "slotworks/smart-ui/options/control-options";

export enum PayoutMode {
    LINES = "lines",
    WAYS = "ways",
    FIXED = "fixed",
    MULTIPLIER_ONLY = "multiplierOnly"
}

export default class PaytableComponent extends PaginationComponent {

    // Automatically set Layers.Background to "paytable" scene when paytable is shown, and "default" when it's hidden
    public static autoShowBackground: boolean = false;

    // TODO: Review whether these should be handled in control
    public static payoutMode: PayoutMode = PayoutMode.LINES;

    // The minimum bet in a ways game (if applicable)
    public static waysBaseBet: number;

    private cachedOptions: ControlOptions;

    constructor(contentLayer: Layers, uiLayer: Layers, uiSceneName: string, mode: PaginationMode = PaginationMode.PAGIFY) {
        super(contentLayer, uiLayer, uiSceneName, mode);

        if (this.shouldGeneratePagePips()) {
            this.generatePagePips();
        }
    }

    public updateOptions(options: ControlOptions) {
        this.cachedOptions = options;

        this.updatePayouts();
    }

    protected update() {
        super.update();

        this.updatePayouts();
    }

    protected onShow() {
        if (PaytableComponent.autoShowBackground && Layers.Background.hasScene("paytable")) {
            Layers.Background.setScene("paytable").execute();
        }
        super.onShow();
    }

    protected onHide() {
        if (PaytableComponent.autoShowBackground && Layers.Background.hasScene("paytable")) {
            Layers.Background.defaultScene().execute();
        }
        super.onHide();
    }

    protected shouldGeneratePagePips() {
        const btnCentre: ButtonElement = this.uiLayer.getButton("page_centre");
        const btnOffset: ButtonElement = this.uiLayer.getButton("page_offset");

        return Boolean(btnCentre && btnOffset);
    }

    protected generatePagePips() {
        const btnCentre: ButtonElement = this.uiLayer.getButton("page_centre");
        const btnOffset: ButtonElement = this.uiLayer.getButton("page_offset");

        const buttonDistance: number = Math.abs(btnOffset.x - btnCentre.x);
        const centreOffset: number = ((buttonDistance * this.totalPages) / 2) - (buttonDistance / 2);

        for (let i = 0; i < this.totalPages; i++) {
            const pageIndex = i;
            const pageName = this.contentSceneNames[pageIndex];

            const button = btnCentre.clone(pageName);
            this.uiLayer.add(button);
            this.uiLayer.addButton(pageName, button);

            button.x += (buttonDistance * i) - centreOffset;

            const flexiButton = this.uiLayer.getFlexiButton(pageName);

            button.setEnabled(true);
            const listener = () => this.jumpToPage(pageIndex);
            button.on(ButtonEvent.CLICK, listener);
            this.pageJumpListeners.set(flexiButton, listener);

            if (pageIndex === this.page) { button.setEnabled(false); }
        }

        btnCentre.destroy(); // These were only needed as a reference
        btnOffset.destroy();
    }

    private updatePayouts() {
        if (this.cachedOptions) {

            const symbols = SlotDefinition.symbolDefinitions;

            for (const symbol of symbols) {
                const payoutsText = this.contentLayer.getFlexiText("payouts_" + symbol.id);

                if (payoutsText) {
                    let payouts = "";
                    if (symbol.payouts) {
                        let output;
                        symbol.payouts.forEach((payout) => {
                            output = this.getPayoutValue(payout);

                            if (payouts) {
                                payouts = output + "\n" + payouts;
                            } else {
                                payouts = output;
                            }
                        });
                    }

                    payoutsText.setText(payouts);
                }

                if (symbol.payouts) {
                    symbol.payouts.forEach((payout, matches) => {
                        const payoutText = this.contentLayer.getFlexiText("payouts_" + symbol.id + "_" + matches);

                        if (payoutText) {
                            payoutText.setText(this.getPayoutValue(payout));
                        }
                    });
                }
            }
        }
    }

    private getPayoutValue(payout: number): string {
        let toReturn;
        let amount;

        const stakePerLine = this.cachedOptions.creditsPerLine.currentValue * this.cachedOptions.creditSizes.currentValue;

        if (PaytableComponent.payoutMode === PayoutMode.FIXED) {
            const betPerLine = (this.cachedOptions.fixedBetCost / this.cachedOptions.selectedLines);
            amount = Math.floor(payout * stakePerLine * betPerLine);
            toReturn = Services.currency.format(amount, false);
        } else if (PaytableComponent.payoutMode === PayoutMode.LINES) {
            amount = payout * stakePerLine;
            toReturn = Services.currency.format(amount, false);
        } else if (PaytableComponent.payoutMode === PayoutMode.WAYS) {
            amount = payout * this.cachedOptions.getTotalStake() / PaytableComponent.waysBaseBet;
            toReturn = Services.currency.format(amount, false);
        } else {
            toReturn = payout;
        }

        return toReturn.toString();
    }
}
